import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Search as SearchIcon, Dice1, Users, BookOpen, Heart, CheckCircle, Star, X, Filter, Loader2, Flame, Zap, ShieldCheck, CheckCircle2, Gift, ChevronDown, ChevronUp, Coins, PlusCircle, ChevronLeft, Youtube } from 'lucide-react';
import { GameCard } from '../components/GameCard';
import { getPopularGames } from '../lib/gameService';
import { 
  toggleOwned, 
  toggleWishlist, 
  togglePlayed, 
  getUserCollectionStatusIds 
} from '../lib/game/collectionService';
import { useAuth } from '../contexts/AuthContext';
import type { Game, UserGame, GameSearchFilters } from '../types/index';
import SEO from '../components/SEO';
import FeedComponent from '../components/feed/FeedComponent';
import GameFilters from '../components/GameFilters';
import useDebounce from '../hooks/useDebounce';
import { supabase } from '../lib/supabase';
import { getUserStreakStatus, getUserStreakXpMultiplier, registerStreakActivity } from '../lib/gamification/streakService';
import { getUserActiveMissions } from '../lib/gamification/missionService';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'react-hot-toast';
import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { HeroBanner } from '@/components/admin/HeroBannersAdminContent';
import YouTubeVideoCard from '@/components/YouTubeVideoCard';

const DEFAULT_BANNER_COLOR_HOME = '#6366F1'; // Cor padrão se não especificada
const DEFAULT_IS_ACTIVE_HOME = true;
const DEFAULT_VISIBILITY = 'all';

// Função auxiliar para converter cor para RGBA
const colorToRgba = (colorString: string, alpha: number): string => {
  if (colorString.startsWith('#')) {
    const hexClean = colorString.replace('#', '');
    let rHex, gHex, bHex;
    if (hexClean.length === 3) {
      rHex = hexClean[0] + hexClean[0];
      gHex = hexClean[1] + hexClean[1];
      bHex = hexClean[2] + hexClean[2];
    } else if (hexClean.length === 6) {
      rHex = hexClean.substring(0, 2);
      gHex = hexClean.substring(2, 4);
      bHex = hexClean.substring(4, 6);
    } else {
      return alpha === 0 ? 'transparent' : colorString; // Hex inválido
    }

    const r = parseInt(rHex, 16);
    const g = parseInt(gHex, 16);
    const b = parseInt(bHex, 16);

    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  // Se não for hex ou a conversão falhar
  return alpha === 0 ? 'transparent' : colorString;
};

// Tipo para o filtro de tag ativo - Adicionado displayValue
interface TagFilter {
  type: 'game' | 'mechanic' | 'category' | 'theme';
  value: string; // ID para game, nome para outros
  displayValue?: string; // Nome para exibir (útil para games)
}

// Definição para os vídeos do YouTube
export interface DisplayYouTubeVideo {
  video_id: string;
  title: string;
  thumbnail_url: string;
  video_url: string;
  published_at: string;
  youtube_channels: {
    channel_id: string;
    channel_name: string;
    custom_url: string | null;
    thumbnail_url: string | null;
  } | null; // channel pode ser null se o join falhar ou não houver canal correspondente
}

const MIN_FILTER_LENGTH = 4;

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [popularGames, setPopularGames] = useState<Game[]>([]);
  const [loadingPopular, setLoadingPopular] = useState(true);
  const [userCollection, setUserCollection] = useState<Record<string, UserGame>>({});
  const [collectionStatusIds, setCollectionStatusIds] = useState<{
    ownedIds: Set<string>;
    wishlistIds: Set<string>;
    playedIds: Set<string>;
  }>({ ownedIds: new Set(), wishlistIds: new Set(), playedIds: new Set() });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [message, setMessage] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);
  const [activeTagFilter, setActiveTagFilter] = useState<TagFilter | null>(null);
  const [showFilterUI, setShowFilterUI] = useState(false);
  const [gameFiltersForUI, setGameFiltersForUI] = useState<GameSearchFilters>({});
  const [gameNameFilter, setGameNameFilter] = useState('');
  const debouncedGameNameFilter = useDebounce(gameNameFilter, 500);
  const effectiveGameNameFilter = 
    debouncedGameNameFilter.trim().length >= MIN_FILTER_LENGTH
    ? debouncedGameNameFilter.trim()
    : undefined;
  // Estados para streak e missões
  const [streakStatus, setStreakStatus] = useState<any>(null);
  const [streakXpMultiplier, setStreakXpMultiplier] = useState<number>(1);
  const [loadingStreak, setLoadingStreak] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [activeMissions, setActiveMissions] = useState<any[]>([]);
  const [loadingMissions, setLoadingMissions] = useState(true);

  // Estados para o Hero Banner Carrossel
  const [heroBanners, setHeroBanners] = useState<HeroBanner[]>([]);
  const [loadingHeroBanners, setLoadingHeroBanners] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 5000, stopOnInteraction: false })]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  // Estados para vídeos do YouTube
  const [youtubeVideos, setYoutubeVideos] = useState<DisplayYouTubeVideo[]>([]);
  const [loadingYoutubeVideos, setLoadingYoutubeVideos] = useState(true);
  const [ytEmblaRef, ytEmblaApi] = useEmblaCarousel({ loop: false, align: 'start' }); // Carrossel para YouTube videos

  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onInit = useCallback((emblaApiLocal: UseEmblaCarouselType[1]) => {
    if (!emblaApiLocal) return;
    setScrollSnaps(emblaApiLocal.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApiLocal: UseEmblaCarouselType[1]) => {
    if (!emblaApiLocal) return;
    setSelectedIndex(emblaApiLocal.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', () => onInit(emblaApi));
    emblaApi.on('reInit', () => onSelect(emblaApi));
    emblaApi.on('select', () => onSelect(emblaApi));
  }, [emblaApi, onInit, onSelect]);

  useEffect(() => {
    if (location.state?.showMessage) {
      setMessage({
        show: true,
        type: location.state.messageType || 'info',
        text: location.state.messageText || ''
      });

      window.history.replaceState({}, document.title);

      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    const loadPopularGames = async () => {
      try {
        setLoadingPopular(true);
        const games = await getPopularGames(6);
        setPopularGames(games);
      } catch (error) {
        console.error('Erro ao carregar jogos populares:', error);
      } finally {
        setLoadingPopular(false);
      }
    };

    loadPopularGames();
  }, []);

  useEffect(() => {
    const loadHeroBanners = async () => {
      setLoadingHeroBanners(true);
      try {
        const { data, error } = await supabase
          .from('hero_banners')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });
        if (error) throw error;
        
        const filteredBanners = (data || []).filter(banner => {
          if (user) { // Usuário está logado
            return banner.visibility === 'all' || banner.visibility === 'logged_in';
          } else { // Usuário está deslogado
            return banner.visibility === 'all' || banner.visibility === 'logged_out';
          }
        });

        const processedBanners: HeroBanner[] = filteredBanners.map(b => {
          let visibilityValue: HeroBanner['visibility'] = DEFAULT_VISIBILITY;
          if (b.visibility === 'all' || b.visibility === 'logged_in' || b.visibility === 'logged_out') {
            visibilityValue = b.visibility;
          }

          return {
            id: b.id || undefined,
            title: b.title || '',
            subtitle: b.subtitle || '',
            image_url: b.image_url || '',
            cta1_text: b.cta1_text || '',
            cta1_link: b.cta1_link || '',
            cta2_text: b.cta2_text || '',
            cta2_link: b.cta2_link || '',
            is_active: typeof b.is_active === 'boolean' ? b.is_active : DEFAULT_IS_ACTIVE_HOME,
            sort_order: b.sort_order === null || b.sort_order === undefined ? 0 : Number(b.sort_order),
            background_color: b.background_color || DEFAULT_BANNER_COLOR_HOME,
            visibility: visibilityValue,
            created_at: b.created_at || undefined,
            updated_at: b.updated_at || undefined,
          }
        });
        setHeroBanners(processedBanners);
      } catch (error) {
        console.error('Erro ao carregar hero banners:', error);
        setHeroBanners([]); // Define como array vazio em caso de erro
      } finally {
        setLoadingHeroBanners(false);
      }
    };
    loadHeroBanners();
  }, [user]);

  useEffect(() => {
    const fetchUserCollectionData = async () => {
      if (user?.id) {
        setLoadingStatus(true);
        setUserCollection({});
        try {
          const statusIds = await getUserCollectionStatusIds(user.id);
          setCollectionStatusIds(statusIds);

          try {
            const { data: userGamesData, error: userGamesError } = await supabase
              .from('user_games')
              .select('*')
              .eq('user_id', user.id);

            if (userGamesError) {
              console.error('Erro ao buscar coleção completa do usuário:', userGamesError);
              throw userGamesError;
            }
            
            const userGamesMap: Record<string, UserGame> = {};
            if (userGamesData) {
              for (const userGame of userGamesData) {
                if (userGame.game_id) {
                  userGamesMap[userGame.game_id] = userGame as UserGame;
                }
              }
            }
            setUserCollection(userGamesMap); 
            console.log('[HomePage] Coleção completa do usuário carregada para lookup:', Object.keys(userGamesMap).length);
            
          } catch (collectionError) {
            console.error('Erro ao buscar detalhes da coleção completa:', collectionError);
            setUserCollection({});
          }

        } catch (error) {
          console.error('Erro geral ao buscar dados da coleção do usuário:', error);
          setCollectionStatusIds({ ownedIds: new Set(), wishlistIds: new Set(), playedIds: new Set() });
          setUserCollection({});
        } finally {
          setLoadingStatus(false);
        }
      } else {
        setCollectionStatusIds({ ownedIds: new Set(), wishlistIds: new Set(), playedIds: new Set() });
        setUserCollection({});
        setLoadingStatus(false);
      }
    };

    fetchUserCollectionData();
  }, [user]);

  const handleToggleOwned = async (gameId: string, isOwned: boolean) => {
    if (!user?.id) return;
    
    try {
      const updatedUserGame = await toggleOwned(user.id, gameId, isOwned);
      
      setUserCollection(prev => {
        const newCollection = { ...prev };
        if (updatedUserGame) {
          newCollection[gameId] = updatedUserGame;
        } else {
          delete newCollection[gameId];
        }
        return newCollection;
      });
      setCollectionStatusIds((prev: typeof collectionStatusIds) => {
        const newOwnedIds = new Set(prev.ownedIds);
        if (updatedUserGame && updatedUserGame.owned) {
          newOwnedIds.add(gameId);
        } else {
          newOwnedIds.delete(gameId);
        }
        return { ...prev, ownedIds: newOwnedIds };
      });
    } catch (error) {
      console.error('Erro ao atualizar coleção:', error);
    }
  };

  const handleToggleWishlist = async (gameId: string, isWishlist: boolean) => {
    if (!user?.id) return;
    
    try {
      const updatedUserGame = await toggleWishlist(user.id, gameId, isWishlist);
      
      setUserCollection(prev => {
        const newCollection = { ...prev };
        if (updatedUserGame) {
          newCollection[gameId] = updatedUserGame;
        } else {
          delete newCollection[gameId];
        }
        return newCollection;
      });
      setCollectionStatusIds((prev: typeof collectionStatusIds) => {
        const newWishlistIds = new Set(prev.wishlistIds);
        if (updatedUserGame && updatedUserGame.wishlist) {
          newWishlistIds.add(gameId);
        } else {
          newWishlistIds.delete(gameId);
        }
        return { ...prev, wishlistIds: newWishlistIds };
      });
    } catch (error) {
      console.error('Erro ao atualizar wishlist:', error);
    }
  };

  const handleTogglePlayed = async (gameId: string, isPlayed: boolean) => {
    if (!user?.id) return;
    
    try {
      const updatedUserGame = await togglePlayed(user.id, gameId, isPlayed);
      
      if (updatedUserGame) {
        setUserCollection(prev => ({
          ...prev,
          [gameId]: updatedUserGame
        }));
        setCollectionStatusIds((prev: typeof collectionStatusIds) => {
          const newPlayedIds = new Set(prev.playedIds);
          if (updatedUserGame.play_count && updatedUserGame.play_count > 0) {
            newPlayedIds.add(gameId);
          } else {
            newPlayedIds.delete(gameId);
          }
          return { ...prev, playedIds: newPlayedIds };
        });
      }
    } catch (error) {
      console.error('Erro ao marcar jogo como jogado:', error);
    }
  };

  // Chamado pelo GameFilters quando qualquer filtro muda
  const handleGameFilterChange = useCallback((newFilters: GameSearchFilters) => {
    console.log('[HomePage] handleGameFilterChange recebido:', newFilters);
    // Atualiza o estado que GameFilters usa
    setGameFiltersForUI(newFilters);

    // Simplificação: Pegar o ÚLTIMO filtro de tag (cat/mech/theme) aplicado 
    // e definir como activeTagFilter para o FeedComponent.
    // Primeiro, detectar qual tipo de array mudou em relação ao estado anterior.
    let changedType: TagFilter['type'] | null = null;
    let changedValue: string | null = null;

    // Compara categorias
    const prevCats = gameFiltersForUI.categories || [];
    const newCats = newFilters.categories || [];
    if (newCats.length > prevCats.length) { // Adicionou categoria
        changedType = 'category';
        changedValue = newCats[newCats.length - 1];
    } else if (newCats.length < prevCats.length) { // Removeu categoria (limpa filtro?)
        // Se a última removida era o filtro ativo, limpa
        const removed = prevCats.find(c => !newCats.includes(c));
        if (activeTagFilter?.type === 'category' && activeTagFilter?.value === removed) {
            setActiveTagFilter(null);
        }
    } // Ignora se não houve mudança ou se foi apenas reordenação
    
    // Compara mecânicas (similar às categorias)
    const prevMechs = gameFiltersForUI.mechanics || [];
    const newMechs = newFilters.mechanics || [];
     if (newMechs.length > prevMechs.length) {
        changedType = 'mechanic';
        changedValue = newMechs[newMechs.length - 1];
    } else if (newMechs.length < prevMechs.length) {
        const removed = prevMechs.find(m => !newMechs.includes(m));
        if (activeTagFilter?.type === 'mechanic' && activeTagFilter?.value === removed) {
            setActiveTagFilter(null);
        }
    }

    // Compara temas (similar às categorias)
    const prevThemes = gameFiltersForUI.themes || [];
    const newThemes = newFilters.themes || [];
    if (newThemes.length > prevThemes.length) {
        changedType = 'theme';
        changedValue = newThemes[newThemes.length - 1];
    } else if (newThemes.length < prevThemes.length) {
        const removed = prevThemes.find(t => !newThemes.includes(t));
         if (activeTagFilter?.type === 'theme' && activeTagFilter?.value === removed) {
            setActiveTagFilter(null);
        }
    }

    // Define o filtro ativo SE um novo foi identificado
    if (changedType && changedValue) {
        setActiveTagFilter({ type: changedType, value: changedValue });
    } 
    // Nota: Filtro de Jogo Específico não é gerenciado por GameFilters, 
    // ele é definido apenas pelo handleTagFilterChange abaixo.

  }, [activeTagFilter, gameFiltersForUI]);

  // Chamado pelo PostItem ao clicar numa tag - Assinatura e lógica atualizadas
  const handleTagFilterChange = useCallback((type: TagFilter['type'], value: string, displayName?: string) => {
    console.log(`[HomePage] Setando filtro direto: ${type} = ${value}, Display: ${displayName}`);
    setActiveTagFilter({ type, value, displayValue: displayName }); // Armazena displayValue
    // Atualizar gameFiltersForUI para refletir o filtro direto nos filtros da UI?
    setGameFiltersForUI(prev => ({ 
        ...prev, 
        categories: type === 'category' ? [value] : [],
        mechanics: type === 'mechanic' ? [value] : [],
        themes: type === 'theme' ? [value] : [],
    }));
    setShowFilterUI(true); // Mostrar UI de filtros quando um filtro é aplicado por clique
    document.getElementById('feed-section')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Chamado pelo GameFilters ou pelo botão "Limpar Filtro" do feed
  const clearActiveFilter = useCallback(() => {
    console.log('[HomePage] Limpando TODOS os filtros (UI e Feed)');
    setActiveTagFilter(null);
    setGameFiltersForUI({}); // Limpa filtros da UI também
    // Não precisa chamar onClearFilters do GameFilters, ele chama este handler
  }, []);

  const clearGameNameFilter = useCallback(() => {
      setGameNameFilter('');
  }, []);

  const feedFilters = {
    tagType: activeTagFilter?.type,
    tagValue: activeTagFilter?.value,
    filterGameName: effectiveGameNameFilter,
  };

  const feedKey = JSON.stringify(feedFilters);

  // Carregar streak
  useEffect(() => {
    if (!user) return;
    setLoadingStreak(true);
    (async () => {
      try {
        const status = await getUserStreakStatus(supabase, user.id);
        setStreakStatus(status);
        const multiplier = await getUserStreakXpMultiplier(supabase, user.id);
        setStreakXpMultiplier(multiplier);
      } catch (err) {
        setStreakStatus(null);
      } finally {
        setLoadingStreak(false);
      }
    })();
  }, [user]);

  // Carregar missões
  useEffect(() => {
    if (!user) return;
    setLoadingMissions(true);
    (async () => {
      try {
        const missions = await getUserActiveMissions(supabase, user.id);
        setActiveMissions(missions.slice(0, 3)); // Mostra só as 3 principais
      } catch (err) {
        setActiveMissions([]);
      } finally {
        setLoadingMissions(false);
      }
    })();
  }, [user]);

  // Função de check-in diário
  const handleDailyCheckIn = async () => {
    if (!user) return;
    setCheckingIn(true);
    try {
      const streakResult = await registerStreakActivity(supabase, user.id, 'login');
      if (streakResult.success) {
        toast.success('Check-in diário realizado com sucesso!');
        // Recarregar streak
        const status = await getUserStreakStatus(supabase, user.id);
        setStreakStatus(status);
        const multiplier = await getUserStreakXpMultiplier(supabase, user.id);
        setStreakXpMultiplier(multiplier);
      } else {
        toast.error(streakResult.error || 'Erro ao registrar check-in.');
      }
    } catch (err) {
      toast.error('Erro inesperado ao registrar check-in.');
    } finally {
      setCheckingIn(false);
    }
  };

  // Card de streak
  const renderStreakCard = () => (
    <Card className="mb-4 dark:bg-gray-800 dark:border-gray-700">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="flex items-center gap-2 dark:text-gray-100">
            <Flame className="h-5 w-5 text-orange-500" /> Streak Diário
          </CardTitle>
          <Button asChild className="w-full sm:w-auto bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 shadow-sm dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600">
            <Link to="/profile/xp-history">Meu Progresso</Link>
          </Button>
        </div>
        <CardDescription className="dark:text-gray-400">Mantenha sua sequência de logins diários para ganhar mais XP!</CardDescription>
      </CardHeader>
      <CardContent className="dark:text-gray-300">
        {loadingStreak ? (
          <div className="flex items-center gap-2 dark:text-gray-400"><Loader2 className="h-4 w-4 animate-spin text-primary dark:text-indigo-400" /> Carregando streak...</div>
        ) : (
          <div>
            {!streakStatus && (
              <div className="mb-4 flex flex-col items-start">
                <span className="text-orange-700 dark:text-orange-400 font-semibold mb-2">Comece seu streak diário agora!</span>
                <Button size="sm" onClick={handleDailyCheckIn} disabled={checkingIn} className="bg-orange-500 hover:bg-orange-600 text-white dark:bg-orange-600 dark:hover:bg-orange-700">
                  {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fazer check-in diário'}
                </Button>
              </div>
            )}
            {streakStatus && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-lg font-semibold dark:text-gray-100">
                    <Flame className="h-5 w-5 text-orange-500" />
                    {streakStatus.current_streak} dias
                  </span>
                  <span className="text-xs text-muted-foreground dark:text-gray-400">Maior streak: {streakStatus.longest_streak} dias</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                    <Zap className="h-4 w-4" /> Multiplicador XP: x{streakXpMultiplier.toFixed(2)}
                  </span>
                  {streakStatus.weekly_protection_available ? (
                    <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400" title="Proteção semanal disponível">
                      <ShieldCheck className="h-4 w-4" /> Proteção semanal disponível
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-gray-400 dark:text-gray-500" title="Proteção semanal já usada nesta semana">
                      <ShieldCheck className="h-4 w-4" /> Proteção usada
                    </span>
                  )}
                  {/* Botão de check-in diário */}
                  {(() => {
                    if (!streakStatus.last_activity_date) {
                      return (
                        <div className="flex items-start">
                          <Button size="sm" onClick={handleDailyCheckIn} disabled={checkingIn} className="bg-orange-500 hover:bg-orange-600 text-white dark:bg-orange-600 dark:hover:bg-orange-700">
                            {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fazer check-in diário'}
                          </Button>
                        </div>
                      );
                    }
                    const todayStr = new Date().toISOString().slice(0, 10);
                    const lastStr = new Date(streakStatus.last_activity_date).toISOString().slice(0, 10);
                    if (todayStr !== lastStr) {
                      return (
                        <div className="flex items-start">
                          <Button size="sm" onClick={handleDailyCheckIn} disabled={checkingIn} className="bg-orange-500 hover:bg-orange-600 text-white dark:bg-orange-600 dark:hover:bg-orange-700">
                            {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fazer check-in diário'}
                          </Button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Card de missões
  const renderMissionsCard = () => (
    <Card className="mb-4 dark:bg-gray-800 dark:border-gray-700">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="flex items-center gap-2 dark:text-gray-100">
            <Star className="h-5 w-5 text-yellow-500" /> Missões Ativas
          </CardTitle>
          <Button asChild className="w-full sm:w-auto bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 shadow-sm dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600">
            <Link to="/profile/missions">Ver todas as missões</Link>
          </Button>
        </div>
        <CardDescription className="dark:text-gray-400">Complete missões para ganhar XP e recompensas!</CardDescription>
      </CardHeader>
      <CardContent className="dark:text-gray-300">
        {loadingMissions ? (
          <div className="flex items-center gap-2 dark:text-gray-400"><Loader2 className="h-4 w-4 animate-spin text-primary dark:text-indigo-400" /> Carregando missões...</div>
        ) : (
          <>
            {activeMissions.length === 0 ? (
              <div className="text-muted-foreground dark:text-gray-400 mb-4">Você não possui missões ativas no momento.</div>
            ) : (
              <div className="space-y-3 mb-4">
                {activeMissions.map((um) => (
                  <div key={um.id} className="p-3 border rounded-lg bg-slate-50 dark:bg-slate-700/50 dark:border-slate-600 flex flex-col gap-1">
                    <div className="flex items-center gap-2 font-semibold dark:text-gray-100">
                      <CheckCircle2 className="h-4 w-4 text-green-500 dark:text-green-400" /> {um.mission?.title || 'Missão'}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">{um.mission?.description}</div>
                    <div className="flex items-center gap-2">
                      {um.mission?.xp_reward > 0 && (
                        <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 text-xs font-semibold"><Star className="h-3 w-3" /> +{um.mission.xp_reward} XP</span>
                      )}
                      {um.mission?.badge_reward_id && (
                        <span className="flex items-center gap-1 text-purple-600 dark:text-purple-400 text-xs font-semibold"><Gift className="h-3 w-3" /> Badge</span>
                      )}
                    </div>
                    <Progress value={(() => {
                      let completed = 0;
                      let total = um.mission?.required_actions?.length || 1;
                      if (um.mission?.required_actions) {
                        um.mission.required_actions.forEach((req: any) => {
                          const current = um.progress?.[req.action_type]?.count || 0;
                          if (current >= req.quantity) completed++;
                        });
                      }
                      return (completed / total) * 100;
                    })()} className="w-full h-1 mt-1" />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  // Função para verificar se uma cor é clara (para ajuste de contraste do texto)
  const isColorLight = (hexColor: string | null | undefined): boolean => {
    if (!hexColor) return false;
    const hex = hexColor.replace('#', '');
    if (hex.length !== 6) return false; // Basic validation for hex length
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return false; // Check if parsing failed
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155; 
  };

  useEffect(() => {
    const loadYoutubeVideos = async () => {
      setLoadingYoutubeVideos(true);
      try {
        const { data, error } = await supabase
          .from('youtube_videos')
          .select(`
            video_id,
            title,
            thumbnail_url,
            video_url,
            published_at,
            youtube_channels (
              channel_id,
              channel_name,
              custom_url,
              thumbnail_url
            )
          `)
          .order('published_at', { ascending: false })
          .limit(15);

        if (error) {
          console.error('Error fetching YouTube videos:', error);
          throw error;
        }
        setYoutubeVideos(data as DisplayYouTubeVideo[] || []);
      } catch (error) {
        console.error('Failed to load YouTube videos:', error);
        setYoutubeVideos([]); // Define como array vazio em caso de erro
      } finally {
        setLoadingYoutubeVideos(false);
      }
    };

    loadYoutubeVideos();
  }, []);

  return (
    <>
      <SEO 
        title="Página Inicial"
        description="Sua plataforma para descobrir, organizar e compartilhar jogos de tabuleiro no Brasil."
      />
      <div className="container mx-auto max-w-6xl px-4 py-6 dark:bg-gray-900">
        {/* Mensagem de sucesso */}
        {message?.show && (
          <div className={`mb-6 p-4 rounded-lg flex items-center ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-800/20 dark:text-green-300 dark:border-green-700' :
            message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-800/20 dark:text-red-300 dark:border-red-700' :
            'bg-blue-50 text-blue-800 border border-blue-200 dark:bg-blue-800/20 dark:text-blue-300 dark:border-blue-700'
          }`}>
            {message.type === 'success' && <CheckCircle className="h-5 w-5 mr-2" />}
            <span>{message.text}</span>
            {message.type === 'success' && message.text.includes('faça login') && (
              <Link to="/signin" className="ml-2 font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300">
                Fazer login agora
              </Link>
            )}
            <button 
              onClick={() => setMessage(null)}
              className="ml-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ×
            </button>
          </div>
        )}

        {/* Hero Section Carrossel */}
        {loadingHeroBanners ? (
          <div className="bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg shadow-lg mb-8 overflow-hidden relative z-0 h-72 flex items-center justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-white" />
          </div>
        ) : heroBanners.length > 0 ? (
          <section className="mb-8 relative z-0">
            <div className="overflow-hidden rounded-lg shadow-lg" ref={emblaRef}>
              <div className="flex">
                {heroBanners.map((banner) => {
                  const baseColorForGradient = banner.background_color || DEFAULT_BANNER_COLOR_HOME;
                  return (
                    <div key={banner.id} className="embla__slide relative flex-[0_0_100%] md:h-[400px]">
                      <div style={{ backgroundColor: baseColorForGradient }} className="h-full">
                        <div className="flex flex-col md:flex-row h-full">
                          <div className="p-6 md:p-12 flex flex-col justify-center md:w-1/2 md:h-full">
                            <h1 className={`text-2xl md:text-4xl font-bold mb-4 ${isColorLight(banner.background_color) ? 'text-gray-800 dark:text-gray-900' : 'text-white'}`}>
                              {banner.title}
                            </h1>
                            <p className={`mb-6 text-sm md:text-base ${isColorLight(banner.background_color) ? 'text-gray-600 dark:text-gray-500' : 'text-indigo-100 dark:text-indigo-200'}`}>
                              {banner.subtitle}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                              <Link
                                to={banner.cta1_link}
                                className={`px-4 py-2 md:px-6 md:py-3 font-medium rounded-lg transition flex items-center justify-center 
                                          ${isColorLight(banner.background_color) ? 
                                            'bg-slate-700 text-white hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-700' : 
                                            'bg-white text-indigo-700 hover:bg-indigo-50 dark:bg-gray-100 dark:text-indigo-700 dark:hover:bg-gray-200'}`}
                              >
                                {banner.cta1_text}
                                <ChevronRight className="h-5 w-5 ml-1" />
                              </Link>
                              <Link
                                to={banner.cta2_link}
                                className={`px-4 py-2 md:px-6 md:py-3 font-medium rounded-lg transition flex items-center justify-center 
                                          ${isColorLight(banner.background_color) ? 
                                            'bg-slate-500 text-white hover:bg-slate-600 dark:bg-slate-400 dark:hover:bg-slate-500' : 
                                            'bg-indigo-700 text-white hover:bg-indigo-800 dark:bg-indigo-500 dark:hover:bg-indigo-600'}`}
                              >
                                {banner.cta2_text}
                                <ChevronRight className="h-5 w-5 ml-1" />
                              </Link>
                            </div>
                          </div>
                          <div className="hidden md:block md:w-1/2 relative md:h-full">
                            <img
                              src={banner.image_url}
                              alt={banner.title}
                              className="h-full w-full object-cover absolute inset-0 z-0" 
                            />
                            <div 
                              className="absolute inset-0 z-10"  // Removido opacity-60
                              style={{
                                // Gradiente da cor base sólida para a cor base transparente
                                backgroundImage: `linear-gradient(to right, ${baseColorForGradient} 20%, ${colorToRgba(baseColorForGradient, 0)} 80%)` 
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {heroBanners.length > 1 && (
              <div className="embla__dots">
                {scrollSnaps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollTo(index)}
                    className={`embla__dot`.concat(index === selectedIndex ? " embla__dot--selected" : "")}
                    aria-label={`Ir para o slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </section>
        ) : (
          // Fallback para o banner estático original se nenhum banner dinâmico for encontrado
          <section className="bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg shadow-lg mb-8 overflow-hidden relative z-0">
            <div className="flex flex-col md:flex-row">
              <div className="p-6 md:p-12 flex flex-col justify-center md:w-1/2">
                <h1 className="text-2xl md:text-4xl font-bold mb-4 dark:text-white">
                  Organize sua coleção de jogos de tabuleiro
                </h1>
                <p className="mb-6 text-indigo-100 dark:text-indigo-200 text-sm md:text-base">
                  Cadastre, organize e descubra novos jogos para sua coleção. Receba recomendações personalizadas baseadas em seus gostos.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={user ? "/collection" : "/signup"}
                    className="px-4 py-2 md:px-6 md:py-3 bg-white text-indigo-600 font-medium rounded-lg hover:bg-indigo-50 transition flex items-center justify-center dark:bg-gray-100 dark:text-indigo-700 dark:hover:bg-gray-200"
                  >
                    {user ? "Minha Coleção" : "Criar Conta"}
                    <ChevronRight className="h-5 w-5 ml-1" />
                  </Link>
                  <Link
                    to="/search"
                    className="px-4 py-2 md:px-6 md:py-3 bg-indigo-700 text-white font-medium rounded-lg hover:bg-indigo-800 transition flex items-center justify-center dark:bg-indigo-500 dark:hover:bg-indigo-600"
                  >
                    <SearchIcon className="h-5 w-5 mr-1" />
                    Buscar Jogos
                  </Link>
                </div>
              </div>
              <div className="hidden md:block md:w-1/2 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 dark:from-indigo-700 to-transparent z-10"></div>
                <img
                  src="/boardverso-home.png"
                  alt="Jogos de tabuleiro"
                  className="h-full w-full object-cover relative"
                />
              </div>
            </div>
          </section>
        )}

        {/* Cards de streak e missões para usuários logados - AGORA LOGO APÓS O BANNER */}
        {user && (
          <div className="mb-8">
            {renderStreakCard()}
            {renderMissionsCard()}
          </div>
        )}

        {/* Seção de Vídeos do YouTube */}
        {!loadingYoutubeVideos && youtubeVideos.length > 0 && (
          <section className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
                <Youtube className="h-6 w-6 mr-2 text-red-600" /> Vídeos Recentes do YouTube
              </h2>
              {/* Pode adicionar um link "Ver Mais" se houver uma página dedicada no futuro */}
            </div>
            <div className="overflow-hidden" ref={ytEmblaRef}>
              <div className="flex -ml-4"> {/* Negative margin to counteract padding on cards for seamless look */}
                {youtubeVideos.map((video) => (
                  <div key={video.video_id} className="embla__slide flex-[0_0_90%] sm:flex-[0_0_45%] md:flex-[0_0_30%] lg:flex-[0_0_23%] pl-4">
                    <YouTubeVideoCard video={video} />
                  </div>
                ))}
              </div>
            </div>
            {/* Adicionar botões de navegação para o carrossel do YouTube se desejar */}
            {/* Exemplo:
            {ytEmblaApi && (
              <div className="flex justify-center mt-4 space-x-2">
                <Button onClick={() => ytEmblaApi.scrollPrev()} disabled={!ytEmblaApi?.canScrollPrev()}>Anterior</Button>
                <Button onClick={() => ytEmblaApi.scrollNext()} disabled={!ytEmblaApi?.canScrollNext()}>Próximo</Button>
              </div>
            )}
            */}
          </section>
        )}
        {loadingYoutubeVideos && (
           <div className="mb-8 p-4 text-center text-gray-500 dark:text-gray-400">Carregando vídeos do YouTube... <Loader2 className="inline h-5 w-5 animate-spin" /></div>
        )}

        {/* Features Section */}
        {!user && (
          <section className="py-12 bg-gray-50 dark:bg-gray-800/50">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-center text-gray-800 dark:text-gray-100 mb-8">
                Tudo que você precisa para sua coleção
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div className="bg-white dark:bg-gray-700 p-5 rounded-lg shadow dark:shadow-gray-900/50">
                  <div className="rounded-full bg-indigo-100 dark:bg-indigo-600/30 p-3 w-12 h-12 flex items-center justify-center mb-4">
                    <BookOpen className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 dark:text-gray-100">Organize sua coleção</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base">
                    Cadastre todos os seus jogos de tabuleiro, mantenha-os organizados e acesse de qualquer lugar.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-700 p-5 rounded-lg shadow dark:shadow-gray-900/50">
                  <div className="rounded-full bg-indigo-100 dark:bg-indigo-600/30 p-3 w-12 h-12 flex items-center justify-center mb-4">
                    <Heart className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 dark:text-gray-100">Crie sua wishlist</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base">
                    Adicione jogos que você deseja comprar à sua wishlist e receba alertas de promoções.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-700 p-5 rounded-lg shadow dark:shadow-gray-900/50">
                  <div className="rounded-full bg-indigo-100 dark:bg-indigo-600/30 p-3 w-12 h-12 flex items-center justify-center mb-4">
                    <Dice1 className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 dark:text-gray-100">Recomendações personalizadas</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base">
                    Receba sugestões de novos jogos baseadas em seus gostos e preferências.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Seção do Feed Social */}
        <section id="feed-section" className="mb-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
              <span className="mr-2">Atividade Recente</span>
              <span className="text-xs font-normal px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full dark:bg-indigo-600/30 dark:text-indigo-300">
                Em tempo real
              </span>
            </h2>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button 
                onClick={() => setShowFilterUI(!showFilterUI)}
                className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 shadow-sm transition-colors dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                <Filter className="h-4 w-4 mr-1" />
                {showFilterUI ? 'Esconder' : 'Mostrar'} Filtros
              </button>
              <Link
                to="/feed"
                className="flex items-center px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md shadow-sm transition-colors justify-center dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <PlusCircle className="h-4 w-4 mr-1" /> Novo Post
              </Link>
            </div>
          </div>

          {/* Input de busca por nome de jogo */}
          <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                <input
                    type="text"
                    placeholder="Buscar posts por jogo marcado..."
                    value={gameNameFilter}
                    onChange={(e) => setGameNameFilter(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-indigo-400 dark:focus:border-indigo-400"
                />
                {gameNameFilter && (
                    <button onClick={clearGameNameFilter} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
                        <X size={16} />
                    </button>
                )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 pl-1">Digite ao menos {MIN_FILTER_LENGTH} caracteres.</p>
          </div>

          {/* Renderiza GameFilters se showFilterUI for true */}
          {showFilterUI && (
              <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <GameFilters 
                      filters={gameFiltersForUI} 
                      showFilters={true} 
                      sortBy={'name'} 
                      setShowFilters={setShowFilterUI} 
                      onFilterChange={handleGameFilterChange} 
                      onSortChange={() => {}} 
                      onClearFilters={clearActiveFilter} 
                      
                      hidePlayerFilter={true}
                      hideTimeFilter={true}
                      hideComplexityFilter={true}
                      hideSortSelector={true}
                  />
              </div>
          )}
          
          {/* Mostra qual filtro está ativo para o FEED - Atualizado */}
          {activeTagFilter && (
              <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-md text-sm text-indigo-800 flex flex-wrap justify-between items-center gap-2 dark:bg-indigo-900/70 dark:text-indigo-300 dark:border-indigo-700">
                <div className="flex items-center">
                  <span className="font-medium bg-indigo-100 dark:bg-indigo-700/50 dark:text-indigo-200 py-1 px-2 rounded-md mr-2">
                    {activeTagFilter.type === 'game' ? 'Jogo' : activeTagFilter.type.charAt(0).toUpperCase() + activeTagFilter.type.slice(1)}:
                  </span>
                  <span className="font-bold dark:text-gray-100">{activeTagFilter.displayValue || activeTagFilter.value}</span>
                </div>
                <button onClick={clearActiveFilter} 
                  className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 text-xs font-semibold flex items-center p-1.5 rounded-md transition-colors dark:text-indigo-300 dark:hover:text-indigo-200 dark:hover:bg-indigo-700/50">
                  <X size={14} className="mr-1"/> Limpar Filtro
                </button>
              </div>
          )}

          {/* Indicador de Filtro por Nome de Jogo */}
          {effectiveGameNameFilter && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800 flex flex-wrap justify-between items-center gap-2 dark:bg-blue-900/70 dark:text-blue-300 dark:border-blue-700">
                <div className="flex items-center">
                  <span className="font-medium mr-2 dark:text-blue-200">Buscando por jogo:</span>
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-700/50 dark:text-blue-100 rounded-md font-bold">
                    "{effectiveGameNameFilter}"
                  </span>
                </div>
                <button onClick={clearGameNameFilter} 
                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 text-xs font-semibold flex items-center p-1.5 rounded-md transition-colors dark:text-blue-300 dark:hover:text-blue-200 dark:hover:bg-blue-700/50">
                  <X size={14} className="mr-1"/> Limpar Busca
                </button>
              </div>
          )}

          {/* Componente Feed - Passa onTagClick atualizado */}
          <div className="pb-4">
            <FeedComponent 
              key={feedKey}
              externalFilters={feedFilters}
              onTagClick={handleTagFilterChange}
              limit={3}
              showCreatePostForm={false}
              enableInfiniteScroll={false}
            />
            
            <div className="mt-6 text-center">
              <Link 
                to="/feed" 
                className="inline-flex items-center justify-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition-colors dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                Ver mais atividades
                <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Seção de Jogos Populares */}
        <section className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100">Jogos Populares</h2>
            <Link to="/ranking" className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center dark:text-indigo-400 dark:hover:text-indigo-300">
              Ver Ranking Completo <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          {loadingPopular || (loadingStatus && user) ? (
            <p className="text-gray-500 dark:text-gray-400">Carregando jogos populares...</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {popularGames.map(game => (
                <GameCard
                  key={game.id}
                  game={game}
                  userGame={userCollection[game.id] || null}
                  isInitiallyOwned={collectionStatusIds.ownedIds.has(game.id)}
                  isInitiallyWishlist={collectionStatusIds.wishlistIds.has(game.id)}
                  isInitiallyPlayed={collectionStatusIds.playedIds.has(game.id)}
                  onToggleOwned={handleToggleOwned}
                  onToggleWishlist={handleToggleWishlist}
                  onTogglePlayed={handleTogglePlayed}
                  isInCollection={
                    !!userCollection[game.id] ||
                    collectionStatusIds.ownedIds.has(game.id) ||
                    collectionStatusIds.wishlistIds.has(game.id) ||
                    collectionStatusIds.playedIds.has(game.id)
                  }
                  user={user}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default HomePage; 